// src/pages/RequirementsFlow.jsx
import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import RequirementsQuestionnaire from '../components/RequirementsQuestionnaire';
import RequirementsGathering from './RequirementsGathering';
import questionnaireService from '../services/questionnaireService';
import QuestionnaireErrorBoundary from '../components/QuestionnaireErrorBoundary';
import { CurrencyProvider } from '../context/CurrencyContext';
import { FeatureExpandProvider } from '../context/FeatureExpandContext';
import { X } from 'lucide-react';

const RequirementsFlow = ({ user, organization }) => {
    const [showQuestionnaire, setShowQuestionnaire] = useState(false);
    const [responses, setResponses] = useState({});
    const [visibilityRules, setVisibilityRules] = useState(null);
    const [loading, setLoading] = useState(true);
    const [hasExistingResponses, setHasExistingResponses] = useState(false);
    const [searchParams, setSearchParams] = useSearchParams();

    useEffect(() => {
        console.log('RequirementsFlow organization:', organization);
    }, [organization]);

    // Check URL parameters and questionnaire status
    useEffect(() => {
        const checkQuestionnaireStatus = async () => {
            if (!organization?.id) return;
            
            try {
                const shouldShowQuestionnaire = searchParams.get('showQuestionnaire') === 'true';
                
                // Get user-specific responses
                const existingResponses = await fetch(
                    `/api/debug/responses/${organization.id}`, 
                    { credentials: 'include' }
                );
    
                if (!existingResponses.ok) {
                    console.error('Failed to fetch responses:', await existingResponses.text());
                    return;
                }
    
                const responseData = await existingResponses.json();
                
                // Transform array of responses into object keyed by question_id
                const responsesObject = Array.isArray(responseData) ? 
                    responseData.reduce((acc, response) => {
                        // Keep the full response object structure
                        acc[response.question_id] = {
                            ...response,
                            // Ensure proper parsing of response values
                            response_value: response.response_value,
                            organization_response: response.organization_response
                        };
                        return acc;
                    }, {}) : 
                    responseData;
                
                // Check if we have any existing responses
                const hasResponses = Object.keys(responsesObject).length > 0;
                setHasExistingResponses(hasResponses);
                
                setResponses(responsesObject);
                
                // Get visibility rules
                const rules = await questionnaireService.getVisibilityRules(organization.id);
                setVisibilityRules(rules);
                setShowQuestionnaire(shouldShowQuestionnaire);
    
            } catch (error) {
                console.error('Error checking questionnaire status:', error);
            } finally {
                setLoading(false);
            }
        };
    
        checkQuestionnaireStatus();
    }, [organization?.id, searchParams]);

    const handleQuestionnaireComplete = async () => {
        try {
            const rules = await questionnaireService.getVisibilityRules(organization.id);
            setVisibilityRules(rules);
            setShowQuestionnaire(false);
            // Remove the query parameter
            searchParams.delete('showQuestionnaire');
            setSearchParams(searchParams);
        } catch (error) {
            console.error('Error updating visibility rules:', error);
        }
    };

    const handleQuestionnaireClose = () => {
        setShowQuestionnaire(false);
        // Remove the query parameter
        searchParams.delete('showQuestionnaire');
        setSearchParams(searchParams);
    };

    const handleResponseUpdate = async (newResponses) => {
        // Ensure newResponses is an object
        const responsesObject = Array.isArray(newResponses) ? 
            newResponses.reduce((acc, response) => {
                acc[response.question_id] = response;
                return acc;
            }, {}) : 
            newResponses;

        setResponses(responsesObject);
        setHasExistingResponses(Object.keys(responsesObject).length > 0);
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-screen">
                <div className="text-lg">Loading...</div>
            </div>
        );
    }

    // Render the main content wrapped in CurrencyProvider
    const renderContent = () => {
        if (showQuestionnaire) {
            return (
                <div className="relative">
                    {/* Close button */}
                    <button
                        onClick={handleQuestionnaireClose}
                        className="absolute top-4 right-4 p-2 rounded-full bg-white shadow-md hover:bg-gray-50"
                        title="Close questionnaire"
                    >
                        <X className="w-5 h-5 text-gray-500" />
                    </button>
                    <QuestionnaireErrorBoundary>
                        <RequirementsQuestionnaire
                            organization={organization}
                            onComplete={handleQuestionnaireComplete}
                            onUpdateResponses={handleResponseUpdate}
                            startInReviewMode={hasExistingResponses}
                            initialResponses={responses}
                            user={user}
                        />
                    </QuestionnaireErrorBoundary>
                </div>
            );
        }

        return (
            <FeatureExpandProvider>
                <RequirementsGathering
                user={user}
                organization={organization}
                visibilityRules={visibilityRules}
            />
            </FeatureExpandProvider>
        );
    };

    return (
        <div className="min-h-screen">
            <CurrencyProvider organization={organization}>
                {renderContent()}
            </CurrencyProvider>
        </div>
    );
};

export default RequirementsFlow;